// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/AllLeaveRequestsList.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-hot-toast";
import { ClipboardList, X, Eye, FileText, Check, Ban, ExternalLink, Search } from "lucide-react";
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import SelectDropdown from "../../customStyles/SelectDropdown";
import PageHeader from "../ui/PageHeader";
import ModernTable from "../ui/ModernTable";
import { formatDateTime } from "../../helpers/formatDateTime";

const PRIMARY_COLOR = "#df1f26";   // Crimson Red Brand Color
const SECONDARY_COLOR = "#031269"; // Dark Blue Brand Color

const AllLeaveRequestsList = ({ user }) => {
  // --- State Management ---
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]); // Used for filtering by employee
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Filtering & Pagination States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDept, setSelectedDept] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [approverNote, setApproverNote] = useState("");
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const [currentPage, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Status Filter Options
  const statusOptions = [
    { label: "All Statuses", id: "all" },
    { label: "Pending Requests", id: "pending" },
    { label: "Approved Requests", id: "approved" },
    { label: "Rejected Requests", id: "rejected" },
    { label: "Cancelled Requests", id: "cancelled" },
  ];

  // ── FETCH EMPLOYEES FOR SEARCH FILTERS ─────────────────────────────────────
  const fetchEmployees = useCallback(async () => {
    try {
      const url = SummaryApi.getAllEmployees?.url || "/api/hr/employees";
      const method = SummaryApi.getAllEmployees?.method || "get";
      const res = await api({ url, method });
      if (res.data.success) {
        setEmployees(res.data.data || []);
      }
    } catch (err) {
      console.error("Error loading filter lists:", err);
    }
  }, []);

  // ── FETCH GLOBAL LEAVE REQUESTS (With Filters and Pagination) ───────────────
  const fetchAllLeaves = useCallback(async () => {
    setIsLoading(true);
    try {
      const baseUrl = SummaryApi.getAllLeaveRequests?.url || "/api/hr/leaves";
      const method = SummaryApi.getAllLeaveRequests?.method || "get";

      // Build global queries: all=true forces HR view [2]
      let url = `${baseUrl}?all=true&page=${currentPage}&limit=${itemsPerPage}`;
      if (selectedStatus !== "all") url += `&status=${selectedStatus}`;

      const res = await api({ url, method });

      if (res.data.success) {
        setRequests(res.data.data || []);
        setTotalItems(res.data.total || res.data.data.length);
      } else {
        toast.error(res.data.message || "Failed to load leave records.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching leaves queue.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedStatus, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchAllLeaves();
  }, [fetchAllLeaves]);

  // Reset pagination to page 1 on filter changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedDept, selectedStatus]);

  // ── DYNAMIC EXTRACT DEPARTMENTS FROM EMPLOYEES LIST ────────────────────────
  const departmentOptions = useMemo(() => {
    const depts = [{ label: "All Departments", id: "all" }];
    const seen = new Set();
    employees.forEach(emp => {
      const dept = emp.departmentId;
      if (dept && dept._id && !seen.has(dept._id)) {
        seen.add(dept._id);
        depts.push({ label: dept.name, id: dept._id });
      }
    });
    return depts;
  }, [employees]);

  // ── LOCAL FILTERING FOR DYNAMIC SEARCH ─────────────────────────────────────
  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      const emp = req.userId || {};
      const fullName = `${emp.firstName || ""} ${emp.lastName || ""}`.toLowerCase();
      
      const matchesSearch = 
        fullName.includes(searchQuery.toLowerCase()) || 
        (emp.userId && emp.userId.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (emp.email && emp.email.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesDept = 
        selectedDept === "all" || 
        (emp.departmentId === selectedDept || emp.departmentId?._id === selectedDept);

      return matchesSearch && matchesDept;
    });
  }, [requests, searchQuery, selectedDept]);

  // --- Drawer Handlers ---
  const openViewDrawer = (request) => {
    setSelectedRequest(request);
    setApproverNote(request.approverNote || "");
    setIsViewOpen(true);
  };

  const closeViewDrawer = () => {
    setIsViewOpen(false);
    setSelectedRequest(null);
    setApproverNote("");
  };

  // ── ADMIN OVERRIDE PROCESS (Approve/Reject directly as Admin/Owner) ────────
  const handleAdminProcessRequest = async (status) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      let url, method;
      
      if (status === "approved") {
        url = SummaryApi.approveLeaveRequest?.url(selectedRequest._id) || `/api/hr/leaves/${selectedRequest._id}/approve`;
        method = SummaryApi.approveLeaveRequest?.method || "put";
      } else {
        url = SummaryApi.rejectLeaveRequest?.url(selectedRequest._id) || `/api/hr/leaves/${selectedRequest._id}/reject`;
        method = SummaryApi.rejectLeaveRequest?.method || "put";
      }

      const res = await api({
        url,
        method,
        data: { approverNote }
      });

      if (res.data.success) {
        toast.success(`Request ${status} successfully as Administrator!`);
        await fetchAllLeaves();
        closeViewDrawer();
      } else {
        toast.error(res.data.message || "Failed to process request.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error processing request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Table Columns Mapping ---
  const columns = [
    {
      key: "employee",
      label: "Employee",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs uppercase" style={{ color: SECONDARY_COLOR }}>
            {row.userId?.profilePicture ? (
              <img src={row.userId.profilePicture} alt="Avatar" className="h-full w-full rounded-full object-cover" />
            ) : (
              `${row.userId?.firstName?.charAt(0) || ""}${row.userId?.lastName?.charAt(0) || ""}`
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-gray-800">{`${row.userId?.firstName || ""} ${row.userId?.lastName || ""}`}</span>
            <span className="text-[10px] text-gray-400 font-mono font-bold">{row.userId?.userId || "No ID"}</span>
          </div>
        </div>
      ),
    },
    {
      key: "leaveType",
      label: "Leave Type",
      render: (row) => (
        <span
          className="px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider"
          style={{
            backgroundColor: `${row.leaveTypeId?.color || "#6366f1"}15`,
            color: row.leaveTypeId?.color || "#6366f1",
            border: `1px solid ${row.leaveTypeId?.color || "#6366f1"}30`,
          }}
        >
          {row.leaveTypeId?.code || "LV"}
        </span>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      render: (row) => {
        const start = formatDateTime(row.startDate, false);
        const end = formatDateTime(row.endDate, false);
        return (
          <span className="font-semibold text-gray-700">
            {row.isHalfDay ? `${start} (Half-Day)` : `${start} - ${end}`}
          </span>
        );
      },
    },
    {
      key: "totalDays",
      label: "Total Days",
      render: (row) => <span className="font-bold text-gray-800">{row.totalDays} Day(s)</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const colors = {
          pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
          approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
          rejected: "bg-red-50 text-red-700 border-red-200",
          cancelled: "bg-gray-100 text-gray-500 border-gray-200"
        };
        return (
          <span className={`px-2.5 py-0.5 rounded border text-xs font-bold uppercase tracking-wider ${colors[row.status]}`}>
            {row.status}
          </span>
        );
      },
    },
    {
      key: "approverId",
      label: "Processed By",
      render: (row) => (
        <span className="text-xs text-gray-500 font-semibold">
          {row.approverId ? `${row.approverId.firstName} ${row.approverId.lastName}` : "Pending Review"}
        </span>
      ),
    },
  ];

  const tableActions = [
    {
      label: "View & Override Request",
      icon: <Eye className="w-4 h-4 text-blue-600" />,
      onClick: (row) => openViewDrawer(row),
    },
  ];

  return (
    <div className="p-4">
      <PageHeader
        title="All Applications"
        description="Global logs and status reports of all leave applications across the entire organization."
        titleIcon={ClipboardList}
      />

      <div className="mt-6">
        {/* Advanced Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-end">
          <div className="w-full">
            <label className="block text-sm font-semibold mb-1.5 text-gray-700">Search Employee</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Name, ID or Email..."
                className="w-full h-10 pl-9 pr-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-100"
              />
            </div>
          </div>

          <div className="w-full">
            <SelectDropdown
              label="Filter by Department"
              options={departmentOptions}
              value={selectedDept}
              onChange={(val) => setSelectedDept(val)}
              labelKey="label"
              valueKey="id"
            />
          </div>

          <div className="w-full">
            <SelectDropdown
              label="Filter by Status"
              options={statusOptions}
              value={selectedStatus}
              onChange={(val) => setSelectedStatus(val)}
              labelKey="label"
              valueKey="id"
            />
          </div>
        </div>

        {/* Global Applications list with loader */}
        <div className="relative overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 transition-all rounded-2xl">
              <span className="text-xs font-bold text-gray-500 animate-pulse tracking-wide">Refreshing global logs...</span>
            </div>
          )}
          <ModernTable
            columns={columns}
            data={filteredRequests} // Using filtered subset
            actions={tableActions}
            selectable={false}
            actionType="dropdown"
            pagination={{
              totalItems,
              itemsPerPage,
              currentPage,
              onPageChange: (page) => setPage(page),
              onItemsPerPageChange: (limit) => {
                setItemsPerPage(limit);
                setPage(1);
              }
            }}
          />
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE UNIFIED LEAVE REVIEW DRAWER */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isViewOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeViewDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-lg bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isViewOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900" style={{ color: SECONDARY_COLOR }}>
                  Review Leave Request
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Admin panel to review, audit, or override leaves.</p>
              </div>
              <button
                type="button"
                onClick={closeViewDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Review Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Employee Summary Card */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-gray-100 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center font-bold text-sm uppercase border border-gray-200">
                    {selectedRequest?.userId?.profilePicture ? (
                      <img src={selectedRequest.userId.profilePicture} alt="Avatar" className="h-full w-full rounded-full object-cover" />
                    ) : (
                      `${selectedRequest?.userId?.firstName?.charAt(0) || ""}${selectedRequest?.userId?.lastName?.charAt(0) || ""}`
                    )}
                  </div>
                  <div>
                    <div className="text-md font-bold text-gray-800">
                      {selectedRequest?.userId ? `${selectedRequest.userId.firstName} ${selectedRequest.userId.lastName}` : ""}
                    </div>
                    <div className="text-xs text-gray-400 font-semibold">{selectedRequest?.userId?.email}</div>
                  </div>
                </div>
              </div>

              {/* Leave details */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Leave Parameters</h4>
                <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm space-y-3 text-sm">
                  <div className="flex justify-between items-center pb-2.5 border-b border-gray-50">
                    <span className="text-gray-500 font-medium">Leave Type:</span>
                    <span 
                      className="px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider"
                      style={{
                        backgroundColor: `${selectedRequest?.leaveTypeId?.color || "#6366f1"}15`,
                        color: selectedRequest?.leaveTypeId?.color || "#6366f1",
                        border: `1px solid ${selectedRequest?.leaveTypeId?.color || "#6366f1"}30`
                      }}
                    >
                      {selectedRequest?.leaveTypeId?.name} ({selectedRequest?.leaveTypeId?.code})
                    </span>
                  </div>

                  <div className="flex justify-between items-center pb-2.5 border-b border-gray-50">
                    <span className="text-gray-500 font-medium">Duration:</span>
                    <span className="font-bold text-gray-800">
                      {selectedRequest?.isHalfDay 
                        ? `${formatDateTime(selectedRequest.startDate, false)} (Half-Day)`
                        : `${formatDateTime(selectedRequest?.startDate, false)} - ${formatDateTime(selectedRequest?.endDate, false)}`
                      }
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Total Days Applied:</span>
                    <span className="font-bold text-gray-800">{selectedRequest?.totalDays} Day(s)</span>
                  </div>
                </div>
              </div>

              {/* Employee written reasoning */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Employee Reason</h4>
                <div className="p-3.5 bg-slate-50/50 rounded-xl border border-gray-100 text-sm text-gray-700 leading-relaxed font-medium">
                  "{selectedRequest?.reason || "No reasoning specified"}"
                </div>
              </div>

              {/* Attached documents list */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Uploaded Certificates</h4>
                {(selectedRequest?.attachments || []).length === 0 ? (
                  <div className="text-xs text-gray-400 italic py-2">No files or certificate proofs attached to this application.</div>
                ) : (
                  <div className="space-y-2">
                    {(selectedRequest?.attachments || []).map((file, idx) => (
                      <a 
                        key={idx} 
                        href={file.fileUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center justify-between p-3 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-xl text-blue-700 text-xs font-semibold transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span className="truncate max-w-[220px]">{file.fileName || "View Certificate Document"}</span>
                        </div>
                        <ExternalLink className="w-4 h-4 shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Approver remarks textarea (Available only in pending mode for admin overrides) */}
              {selectedRequest?.status === "pending" && (
                <div className="space-y-2 border-t border-gray-100 pt-5">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Admin Notes / Remarks (Optional)</label>
                  <textarea
                    rows={4}
                    value={approverNote}
                    onChange={(e) => setApproverNote(e.target.value)}
                    placeholder="Enter administrative override remarks or reason..."
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-100"
                  />
                </div>
              )}
            </div>

            {/* Drawer Footer Buttons */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeViewDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>

              {selectedRequest?.status === "pending" ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleAdminProcessRequest("rejected")}
                    disabled={isSubmitting}
                    className="flex items-center gap-1.5 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold transition-colors"
                  >
                    <Ban className="w-4 h-4" />
                    <span>Admin Reject</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAdminProcessRequest("approved")}
                    disabled={isSubmitting}
                    className="flex items-center gap-1.5 px-5 py-2.5 text-white rounded-lg text-sm font-bold transition-colors hover:opacity-90"
                    style={{ backgroundColor: SECONDARY_COLOR }}
                  >
                    <Check className="w-4 h-4" />
                    <span>Admin Approve</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={closeViewDrawer}
                  className="px-5 py-2.5 text-white text-sm font-bold rounded-lg transition-colors hover:bg-secondary"
                  style={{ backgroundColor: PRIMARY_COLOR }}
                >
                  Close Details
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllLeaveRequestsList;